import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Button, Container, Snackbar, Typography, alpha, useTheme } from '@mui/material'

import { apiActions } from '../redux'
import { DependencyTrackAppBar, VulnClientBoard } from '../components/dependency'
import { resolveBoardAccent } from '../components/SocrBoardStatusBar'
import { buildSocrVulnBoardViewModel } from '../utils/socrVulnBoard'

const formatDtRefreshMessage = (result) => {
  if (!result || typeof result !== 'object') return 'Dependency-Track sync finished · code tree reloaded'
  const scope = result.scope === 'one' ? 'one repo' : 'all repos'
  const added = Number(result.added) || 0
  const removed = Number(result.removed) || 0
  const renamed = Number(result.renamed) || 0
  const kept = Number(result.kept) || 0
  const touched = Number(result.repositoriesTouched) || 0
  const unlinked = Array.isArray(result.unlinkedDtProjectNames)
    ? result.unlinkedDtProjectNames.length
    : 0
  const parts = [
    `DT sync (${scope}): ${touched} repo${touched === 1 ? '' : 's'}`,
    `+${added}`,
    `−${removed}`,
    `↻${renamed}`,
    `${kept} kept`
  ]
  if (unlinked) parts.push(`${unlinked} unlinked`)
  parts.push('code tree reloaded')
  return parts.join(' · ')
}

const formatBitbucketRefreshMessage = (result) => {
  if (!result || typeof result !== 'object') return 'Bitbucket sync finished · code tree reloaded'
  if (result.continuedInBackground) {
    const scope = result.scope === 'one' ? 'one repo' : 'all repos'
    return `Bitbucket runtime scan continues server-side (${scope}) · code tree reloaded — refresh again shortly if TFMs still look stale`
  }
  const scope = result.scope === 'one' ? 'one repo' : 'all repos'
  const checked = Number(result.repositoriesChecked) || 0
  const updated = Number(result.updated) || 0
  const unchanged = Number(result.unchanged) || 0
  const notFound = Number(result.notFound) || 0
  const failed = Number(result.failed) || 0
  const includeRuntimes = Boolean(result.includeRuntimes)
  const upserted = Number(result.runtimesUpserted) || 0
  const removed = Number(result.runtimesRemoved) || 0
  const parts = [
    `Bitbucket sync (${scope}${includeRuntimes ? ' + runtimes' : ''}): ${checked} checked`,
    `${updated} updated`,
    `${unchanged} unchanged`
  ]
  if (includeRuntimes) parts.push(`runtimes +${upserted}/−${removed}`)
  if (notFound) parts.push(`${notFound} not found`)
  if (failed) parts.push(`${failed} failed`)
  parts.push('code tree reloaded')
  return parts.join(' · ')
}

/**
 * Shared code-tree board layout for Vulnerabilities and Upgrades.
 * Catalog + code tree load on mount; vulnerabilities mode then fetches DT project
 * severity so findings join without waiting for Refresh from DT.
 * @param {{ mode?: 'vulnerabilities'|'upgrades' }} props
 */
const DependencyTrackView = ({ mode = 'vulnerabilities' }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const boardId = mode === 'upgrades' ? 'upgrades' : 'vulnerabilities'
  const boardAccent = resolveBoardAccent(theme, boardId)
  const isUpgrades = mode === 'upgrades'
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  const { user: authUser } = useSelector((state) => state.auth || {})
  const {
    socrClientTree = [],
    socrCatalogReady = false,
    socrClientsLoading = false,
    socrCodeTree = [],
    socrCodeTreeFetchedAt = null,
    socrCodeTreeError = null,
    dtProjects = [],
    dtProjectsFetchedAt = null,
    dtProjectsError = null,
    socrDtRefreshLoading = false,
    socrDtRefreshPhase = null,
    socrDtRefreshResult = null,
    socrDtRefreshError = null,
    socrDtRefreshRepositoryUuid = null,
    socrBitbucketRefreshLoading = false,
    socrBitbucketRefreshPhase = null,
    socrBitbucketRefreshResult = null,
    socrBitbucketRefreshError = null,
    socrBitbucketRefreshRepositoryUuid = null
  } = useSelector((state) => state.api || {})

  // Catalog + code tree — Redux is not persisted, so a hard reload must refetch here
  // (not only when the dashboard was visited first).
  useEffect(() => {
    dispatch(apiActions.fetchSocrClients({ force: false }))
    dispatch(apiActions.fetchSocrCodeTree({ force: false }))
  }, [dispatch])

  // After code tree (DT project names) is available, load DT severity for the vulns board.
  useEffect(() => {
    if (mode !== 'vulnerabilities') return
    if (socrCodeTreeFetchedAt == null) return
    dispatch(apiActions.fetchDtProjects({ force: false }))
  }, [dispatch, mode, socrCodeTreeFetchedAt])

  const handleRefreshDt = useCallback(() => {
    dispatch(apiActions.refreshSocrDtProjects())
  }, [dispatch])

  const handleRefreshRepoDt = useCallback((repositoryUuid) => {
    if (!repositoryUuid) return
    dispatch(apiActions.refreshSocrDtProjects({ repositoryUuid }))
  }, [dispatch])

  // Board-level: metadata only (fast catalog). Per-repo: optional .csproj TFM rescan.
  const handleRefreshBitbucket = useCallback(() => {
    dispatch(apiActions.refreshSocrBitbucket({ includeRuntimes: false }))
  }, [dispatch])

  const handleRefreshRepoBitbucket = useCallback((repositoryUuid) => {
    if (!repositoryUuid) return
    dispatch(apiActions.refreshSocrBitbucket({ repositoryUuid, includeRuntimes: true }))
  }, [dispatch])

  useEffect(() => {
    if (socrDtRefreshError) {
      setSnackbar({ open: true, message: socrDtRefreshError })
      return
    }
    if (socrDtRefreshResult && !socrDtRefreshLoading) {
      setSnackbar({ open: true, message: formatDtRefreshMessage(socrDtRefreshResult) })
    }
  }, [socrDtRefreshResult, socrDtRefreshError, socrDtRefreshLoading])

  useEffect(() => {
    if (socrBitbucketRefreshError) {
      setSnackbar({ open: true, message: socrBitbucketRefreshError })
      return
    }
    if (socrBitbucketRefreshResult && !socrBitbucketRefreshLoading) {
      setSnackbar({ open: true, message: formatBitbucketRefreshMessage(socrBitbucketRefreshResult) })
    }
  }, [socrBitbucketRefreshResult, socrBitbucketRefreshError, socrBitbucketRefreshLoading])

  const viewModel = useMemo(
    () =>
      buildSocrVulnBoardViewModel({
        clientTree: socrClientTree,
        codeTree: socrCodeTree,
        dtProjects,
        mode
      }),
    [socrClientTree, socrCodeTree, dtProjects, mode]
  )

  const waitingOnCatalog = socrClientsLoading && !socrCatalogReady
  const waitingOnCodeTree = socrCodeTreeFetchedAt == null && !socrCodeTreeError
  // Hold the vulns board until DT severity arrives (or errors) after code tree.
  const waitingOnDt =
    !isUpgrades &&
    socrCodeTreeFetchedAt != null &&
    dtProjectsFetchedAt == null &&
    !dtProjectsError
  const initialLoading = waitingOnCatalog || waitingOnCodeTree || waitingOnDt
  const loadError = socrCodeTreeError || dtProjectsError

  const retryBootstrapReads = useCallback(() => {
    dispatch(apiActions.fetchSocrClients({ force: true }))
    dispatch(apiActions.fetchSocrCodeTree({ force: true }))
    if (mode === 'vulnerabilities') {
      dispatch(apiActions.fetchDtProjects({ force: true }))
    }
  }, [dispatch, mode])

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default'
      }}
    >
      <DependencyTrackAppBar
        props={{
          authUser,
          mappingsFetchedAt: socrCodeTreeFetchedAt,
          mode,
          trackedCount: viewModel.trackedCount,
          untrackedCount: viewModel.untrackedCount,
          untracked: viewModel.untracked,
          boardSeverity: viewModel.boardSeverity,
          needsUpgradeCount: viewModel.needsUpgradeCount,
          summaryReady: !initialLoading
        }}
        controls={{ dispatch }}
      />

      <Container maxWidth='xl' sx={{ py: { xs: 2, sm: 2.5 }, px: { xs: 1.5, sm: 2 } }}>
        {loadError
          ? (
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
                backgroundColor: alpha(theme.palette.error.main, 0.1)
              }}
            >
              <Typography variant='subtitle2' sx={{ color: theme.palette.error.light, fontWeight: 800, mb: 0.75 }}>
                {isUpgrades ? 'Couldn’t load upgrades board data' : 'Couldn’t load vulnerability board data'}
              </Typography>
              <Typography variant='body2' sx={{ color: alpha('#fff', 0.85), mb: 1.25 }}>
                {loadError}
              </Typography>
              <Button variant='outlined' size='small' color='error' onClick={retryBootstrapReads}>
                Retry
              </Button>
            </Box>
            )
          : null}

        {initialLoading
          ? (
            <Box
              sx={{
                p: 3.5,
                borderRadius: 2,
                border: `1px dashed ${alpha(boardAccent, 0.35)}`,
                textAlign: 'center',
                backgroundColor: alpha('#000', 0.12)
              }}
            >
              <Typography variant='body1' sx={{ color: alpha('#fff', 0.9), fontWeight: 700, mb: 0.5 }}>
                {isUpgrades ? 'Loading upgrades board…' : 'Loading vulnerability board…'}
              </Typography>
              <Typography variant='caption' sx={{ color: alpha('#fff', 0.55) }}>
                {waitingOnCatalog ? 'Loading SOCR catalog…' : 'Loading SOCR code tree…'}
              </Typography>
            </Box>
            )
          : (
            <VulnClientBoard
              mode={mode}
              tracked={viewModel.tracked}
              loading={false}
              onRefreshDt={handleRefreshDt}
              onRefreshRepoDt={handleRefreshRepoDt}
              refreshingDt={socrDtRefreshLoading}
              refreshDtPhase={socrDtRefreshPhase}
              refreshingRepoUuid={socrDtRefreshRepositoryUuid}
              onRefreshBitbucket={handleRefreshBitbucket}
              onRefreshRepoBitbucket={handleRefreshRepoBitbucket}
              refreshingBitbucket={socrBitbucketRefreshLoading}
              refreshBitbucketPhase={socrBitbucketRefreshPhase}
              refreshingBitbucketRepoUuid={socrBitbucketRefreshRepositoryUuid}
            />
            )}
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5500}
        onClose={() => {
          setSnackbar((s) => ({ ...s, open: false }))
          dispatch(apiActions.clearSocrDtRefreshResult())
          dispatch(apiActions.clearSocrBitbucketRefreshResult())
        }}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  )
}

const UpgradesView = () => <DependencyTrackView mode='upgrades' />
const VulnerabilitiesView = () => <DependencyTrackView mode='vulnerabilities' />

export { DependencyTrackView, UpgradesView, VulnerabilitiesView }
